"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { ProgramCardMetadata } from "@/types/programCard";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { handleDownload } from "@/utils/utils";
import { useRouter } from "next/navigation";
import React from "react";
import { useDispatch } from "react-redux";
import ExploreCourses1 from "./ExploreCourses1";

interface ProgramCardsData {
  id: number;
  page_id: number;
  component_id: number;
  title: string;
  title_text_color: string;
  desktop_background_color?: string;
  mobile_background_color?: string;
  card_background_color: string;
  load_more_text: string;
  load_more_icon?: string; // Deprecated, kept for backwards compatibility
  load_more_desktop_icon?: string;
  load_more_desktop_icon_alt?: string;
  load_more_mobile_icon?: string;
  load_more_mobile_icon_alt?: string;
  load_more_text_color?: string; // Deprecated, kept for backwards compatibility
  load_more_background_color?: string; // Deprecated, kept for backwards compatibility
  load_more_desktop_text_color?: string;
  load_more_mobile_text_color?: string;
  load_more_desktop_background_color?: string;
  load_more_mobile_background_color?: string;
  load_more_button_class_type?: string;
  load_more_arrow_variant?: string;
  metadata: ProgramCardMetadata[];
  variant: string;
  status: number;
  is_deleted: number;
  created_by: string | null;
  updated_by: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ProgramCards1Props {
  /** Live page / admin preview pass the program_cards row; sidebar preview passes `{ data: row }`. */
  data: ProgramCardsData | { data: ProgramCardsData };
  /** Blog article layout: align with content column (ExploreCourses1 blogRail). */
  blogRail?: boolean;
  /** Blog Program Cards (variant 5): enable carousel when ExploreCourses1 has multiple cards. */
  blogCarousel?: boolean;
  /** From page shell — blog pages show all cards without the marketing “Load more” slice. */
  pageCategory?: string;
}

const ProgramCards1: React.FC<ProgramCards1Props> = ({
  data,
  blogRail = false,
  blogCarousel = false,
  pageCategory,
}) => {
  const programData =
    data && typeof data === "object" && "data" in data && (data as { data: ProgramCardsData }).data != null
      ? (data as { data: ProgramCardsData }).data
      : (data as ProgramCardsData);
  const router = useRouter();
  const dispatch = useDispatch();

  if (!programData) {
    return null;
  }

  const handleBrochureDownload = (courseId?: string) => {
    const course = programData.metadata.find((item) => item.id === courseId);

    if (course?.meta_brochure_url) {
      // Track brochure download event
      ctaClick({
        section: "explore_courses_section",
        component: "program_card",
        label:
          course.meta_short_course_name ||
          course.meta_full_course_name ||
          courseId ||
          "brochure",
        step: "",
        entity: "brochure_download",
        landing_url: course.meta_brochure_url,
      });


      if (course.meta_brochure_url) {
        handleDownload(
          course.meta_brochure_url,
          `${course.meta_short_course_name || "brochure"}.pdf`
        );
      } 
    } else {
      console.log("Brochure URL not found for course:", courseId);
    }
  };

  const handleKnowMore = (courseId?: string) => {
    // Find the course metadata by id
    const course = programData.metadata.find((item) => item.id === courseId);

    if (!course) {
      return;
    }

    const landingUrl = course.meta_slug
      ? `/${course.meta_slug}`
      : course.meta_url || "";

    ctaClick({
      section: "explore_courses_section",
      component: "program_card",
      label:
        course.meta_short_course_name ||
        course.meta_full_course_name ||
        courseId ||
        "know_more",
      step: "",
      entity: "know_more_button",
      landing_url: landingUrl,
    });

    // Priority 1: Redirect to slug if present (internal route)
    if (course.meta_slug) {
      router.push(`/${course.meta_slug}`);
      return;
    }

    // Priority 2: Redirect to meta_url if present (external URL)
    if (course.meta_url) {
      window.open(course.meta_url, "_blank", "noopener,noreferrer");
      return;
    }

    // fallback to opening lead drop
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }),);
  };

  // Card click handler - same behavior as "Know More"
  const handleCardClick = (courseId?: string) => {
    handleKnowMore(courseId);
  };

  return (
    <ExploreCourses1
      data={programData}
      onBrochureDownload={handleBrochureDownload}
      onKnowMore={handleKnowMore}
      onCardClick={handleCardClick}
      blogRail={blogRail}
      blogCarousel={blogCarousel}
      pageCategory={pageCategory}
    />
  );
};

export default ProgramCards1;
